import { Controller } from "@hotwired/stimulus";

// A Wistia video in book content (assets/_video_embed, rendered server-side by
// Asset::Embeds). Replaces the Vue video-content component's behavior:
//   * player options: brand player colour + the viewer's email (body data-email)
//   * resize the 4:3 placeholder to the video's real aspect once it loads
//   * deep link #<id> or #<id>/<time> (e.g. #9iqqudhi7i/30s): scroll here and
//     autoplay from that time
//   * #showVideoId: show this video's 30s deep link, for instructors to copy
// Wistia's E-v1 script (loaded by the reader layout) finds the
// .wistia_embed.wistia_async_<id> element; _wq queues options and callbacks
// whether or not E-v1 has loaded yet.
export default class extends Controller {
  static targets = ["link", "frame"];
  // popover: a thumbnail that opens a lightbox (Wistia videoFoam sizes it)
  static values = { id: String, popover: Boolean };

  connect() {
    const options = { playerColor: "40757a" };
    const email = document.body.dataset.email;
    if (email) options.email = email;

    // Deep link: start time + autoplay go in as embed options (as the old Vue
    // component did), which Wistia applies whenever the media data arrives; the
    // browser may still block an unmuted autoplay. The scroll waits for the page
    // to settle (the theme controllers rebuild the content above at connect), not
    // for the player, so it doesn't depend on Wistia at all.
    const deepLink = this.#deepLink();
    if (deepLink) {
      options.time = deepLink.seconds;
      options.autoPlay = true;
      this.#afterLoad(() => this.element.scrollIntoView({ block: "center" }));
    }

    window._wq = window._wq || [];
    window._wq.push({
      id: this.idValue,
      options,
      onReady: (video) => {
        const aspect = video.aspect();
        if (aspect && this.hasFrameTarget && !this.popoverValue) {
          this.frameTarget.style.paddingTop = `${Math.round((1 / aspect) * 10000) / 100}%`;
        }
      }
    });

    if (window.location.hash === "#showVideoId" && this.hasLinkTarget) {
      this.linkTarget.textContent = `${window.location.href.split("#")[0]}#${this.idValue}/30s`;
    }

    // A deep link followed on the same page (an in-content link to
    // #<id>/<time>, or the hash edited in the address bar) doesn't reload, so
    // connect() never sees it. A click is a user gesture, so play is allowed.
    this.onHashChange = () => {
      const link = this.#deepLink();
      if (!link) return;
      this.element.scrollIntoView({ behavior: "smooth", block: "center" });
      const play = (video) => { video.time(link.seconds); video.play(); };
      const video = window.Wistia?.api?.(this.idValue);
      if (video) play(video);
      else window._wq.push({ id: this.idValue, onReady: play });
    };
    window.addEventListener("hashchange", this.onHashChange);
  }

  disconnect() {
    window.removeEventListener("hashchange", this.onHashChange);
  }

  #afterLoad(callback) {
    const later = () => setTimeout(callback, 300);
    if (document.readyState === "complete") later();
    else window.addEventListener("load", later, { once: true });
  }

  // "#<id>" or "#<id>/<time>" for this video ("30s", "1m5s" or plain seconds); null otherwise
  #deepLink() {
    const [id, time = "0"] = window.location.hash.replace(/^#/, "").split("/");
    if (id !== this.idValue) return null;
    const [, m = 0, s = 0] = time.match(/^(?:(\d+)m)?(?:(\d+)s?)?$/) || [];
    return { seconds: Number(m) * 60 + Number(s) };
  }
}
